import ProposalSectionCard from "@/components/proposal/ProposalSectionCard";
import ProposalIntroMagazineContent from "@/components/proposal/ProposalIntroMagazineContent";
import { PROPOSAL_COPY_FONT } from "@/lib/proposalTheme";
import { trpc } from "@/lib/trpc";
import { buildDtoolsClarityTabIntro } from "@shared/dtoolsClarityTabContent";
import { DEFAULT_PROPOSAL_INTRO_SETTINGS, type ProposalIntroSettings } from "@shared/proposalIntroSettings";
import { Loader2 } from "lucide-react";

type Props = {
  customerIntroduction?: string | null;
  /** When set, skips published fetch (admin preview / tests). */
  introContent?: ProposalIntroSettings;
  /** Retail / catalogue proposals: hint below company sections. */
  solutionTabHint?: string | null;
};

/** Clarity company introduction tab — shared by projector and retail premium proposals. */
export default function ProposalClarityTabPanel({
  customerIntroduction,
  introContent,
  solutionTabHint = "Select your solution tab above to review products, pricing, and approval.",
}: Props) {
  const publishedQ = trpc.proposalIntroSettings.getPublished.useQuery(undefined, {
    enabled: introContent == null,
  });
  const intro = buildDtoolsClarityTabIntro(customerIntroduction);
  const content = introContent ?? publishedQ.data ?? DEFAULT_PROPOSAL_INTRO_SETTINGS;
  const loading = introContent == null && publishedQ.isLoading;

  return (
    <section className="space-y-6" data-testid="proposal-tab-clarity" style={{ fontFamily: PROPOSAL_COPY_FONT }}>
      {intro ? (
        <ProposalSectionCard eyebrow="Your proposal" title="Prepared for you" variant="elevated">
          <p>{intro}</p>
        </ProposalSectionCard>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading introduction…
        </div>
      ) : (
        <ProposalIntroMagazineContent content={content} />
      )}

      {solutionTabHint ? <p className="text-sm text-muted-foreground">{solutionTabHint}</p> : null}
    </section>
  );
}
